import { useCallback } from "react";
import { useAppIdToken } from "../auth/useAppIdToken";
import { logActivity } from "../api/activityLog";
import type { Perspective, QuestionConfig } from "../reviews";

export type AiScoredAnswer = { score: number; note: string };
export type AiScoreResult = Record<string, AiScoredAnswer>;

export function useReviewAssist() {
  const getIdToken = useAppIdToken();

  const scoreReview = useCallback(
    async (
      perspective: Perspective,
      initiative: { label: string; value: string }[],
      questions: QuestionConfig[]
    ): Promise<AiScoreResult> => {
      const idToken = await getIdToken();
      const res = await fetch("/api/review-assist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Id-Token": idToken,
        },
        body: JSON.stringify({
          mode: "score",
          perspective,
          initiative,
          questions: questions.map((q) => ({
            key: q.key,
            label: q.label,
            prompt: q.prompt,
            consider: q.consider,
            options: q.options,
          })),
        }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.error ?? `AI review request failed (${res.status}).`);
      }
      const { answers } = (await res.json()) as { answers: AiScoreResult };
      void logActivity("Feature", "AIReviewScore", `Ran AI-assisted ${perspective} review scoring`);
      return answers;
    },
    [getIdToken]
  );

  return { scoreReview };
}
